/**
 * Token API types — mirror the mobile app's token.types (same backend contract).
 * The status enum here is the authoritative one used against this backend
 * (resolves MODULE_API_PLAN.md O1).
 */
import { brand } from '../theme/theme';

export type TokenStatus =
  | 'booked' | 'waiting' | 'in_progress' | 'completed' | 'hold' | 'skipped' | 'cancelled' | 'no_show';
export type TokenType = 'pre_booked' | 'walk_in';

export interface TokenCustomer {
  id: string | null;
  phone_number: string;
  country_code: string;
  display_name: string | null;
  profile_image_url: string | null;
}

export interface TokenProvider {
  id: string;
  user_id: string;
  display_name: string | null;
  initials: string;
  profile_image_url: string | null;
}

export interface Token {
  id: string;
  queue_id: string;
  token_number: number;
  status: TokenStatus;
  token_type: TokenType;
  scheduled_date: string;
  estimated_time: string | null;
  estimated_wait_mins: number | null;
  customer: TokenCustomer;
  provider: TokenProvider | null;
  payment_amount: number | null;
  note: string | null;
  total_visits: number;
  last_visit: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface TokensSummary {
  total: number;
  pending: number;
  serving: number;
  completed: number;
  skipped: number;
  cancelled: number;
}

export interface CreateWalkInTokenRequest {
  customer_name: string;
  customer_phone: string;
  country_code?: string;
  scheduled_date?: string;
}

export interface CompleteTokenRequest {
  payment_amount?: number;
  note?: string;
}

// ---- display config ----
export type TokenFilter = 'all' | 'pending' | 'completed' | 'skipped';

export const TOKEN_STATUS_META: Record<TokenStatus, { label: string; color: string; group: TokenFilter | 'other' }> = {
  booked: { label: 'Booked', color: brand.status.booked, group: 'pending' },
  waiting: { label: 'Waiting', color: brand.status.waiting, group: 'pending' },
  in_progress: { label: 'Serving', color: brand.status.in_progress, group: 'pending' },
  hold: { label: 'On Hold', color: brand.status.hold, group: 'pending' },
  completed: { label: 'Completed', color: brand.status.completed, group: 'completed' },
  skipped: { label: 'Skipped', color: brand.status.skipped, group: 'skipped' },
  cancelled: { label: 'Cancelled', color: brand.status.cancelled, group: 'other' },
  no_show: { label: 'No Show', color: brand.status.no_show, group: 'other' },
};

export const PENDING_STATUSES: TokenStatus[] = ['booked', 'waiting', 'in_progress', 'hold'];

export const tokenCustomerName = (t: Token): string =>
  t.customer?.display_name || t.customer?.phone_number || 'Walk-in customer';
